"use client";
import "@/components/home/how-to-shell.css";
import "@/components/ui/atoms/categories/data-display.css";
import "@/components/ui/atoms/categories/layout.css";

import { GUIDE_CHAPTERS, GUIDE_TITLE } from "@/components/home/how-to-content";
import { routes } from "@/lib/routing/routes";

import Link from "next/link";
import { memo, useEffect, useState } from "react";

/**
 * GuideRail — the guide's chapter sidebar. On chapter pages it scroll-spies
 * the sections: the link of the section in view takes the primary tone.
 */
export const GuideRail = memo(function GuideRail({ currentSlug }: { currentSlug: string | null }) {
    const currentChapter = currentSlug ? GUIDE_CHAPTERS.find((chapter) => chapter.slug === currentSlug) : null;
    const sectionIds = currentChapter?.sections?.map((section) => section.id) ?? [];

    const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);

    // Scroll-spy: the active section is the last one whose top has crossed
    // the upper third of the viewport.
    useEffect(() => {
        if (sectionIds.length === 0) {
            return;
        }
        let raf = 0;
        const update = () => {
            raf = 0;
            const line = window.innerHeight * 0.35;
            let current = sectionIds[0];
            for (const id of sectionIds) {
                const el = document.getElementById(id);
                if (!el) {
                    continue;
                }
                if (el.getBoundingClientRect().top <= line) {
                    current = id;
                }
            }
            setActiveId((prev) => (prev === current ? prev : current));
        };
        const onScroll = () => {
            if (!raf) {
                raf = requestAnimationFrame(update);
            }
        };
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
        // sectionIds derives fully from currentSlug.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSlug]);

    return (
        <aside className="dw-howto__rail" aria-label="Guide chapters" data-component="GuideRail" data-location="src/components/home/guide-rail.tsx">
            <p className="dw-text-lead" data-tone="foreground">{GUIDE_TITLE}</p>
            <Link href={routes.crm.signup} className="dw-howto__rail-cta dw-text-caption dw-tabular-nums">{"Try it in DisputeWell →"}</Link>

            <nav className="dw-howto__toc">
                <Link href="/how-to" className="dw-list__item dw-text-body" data-row="" data-interactive="true" data-selected={currentSlug === null ? "true" : undefined} aria-current={currentSlug === null ? "page" : undefined} data-tone="foreground">
                    <span className="dw-list__lead dw-text-caption dw-tabular-nums" data-tone="muted">·</span>
                    <span className="dw-list__text">{"Introduction"}</span>
                </Link>
                {GUIDE_CHAPTERS.map((chapter) => (
                    <div key={chapter.slug} className="dw-howto__toc-group">
                        {chapter.status === "live" ? (
                            <Link href={`/how-to/${chapter.slug}`} className="dw-list__item dw-text-body" data-row="" data-interactive="true" data-selected={chapter.slug === currentSlug ? "true" : undefined} aria-current={chapter.slug === currentSlug ? "page" : undefined} data-tone="foreground">
                                <span className="dw-list__lead dw-text-caption dw-tabular-nums" data-tone="muted">{chapter.numeral}</span>
                                <span className="dw-list__text">{chapter.title}</span>
                            </Link>
                        ) : (
                            <span className="dw-list__item dw-text-body" data-row="" data-tone="muted">
                                <span className="dw-list__lead dw-text-caption dw-tabular-nums">{chapter.numeral}</span>
                                <span className="dw-list__text">{chapter.title}</span>
                                <span className="dw-list__trail dw-text-caption dw-tabular-nums">{"Soon"}</span>
                            </span>
                        )}
                        {chapter.slug === currentSlug && currentChapter?.sections ? (
                            <ul className="dw-howto__toc-sections">
                                {currentChapter.sections.map((section) => (
                                    <li key={section.id}>
                                        <a href={`#${section.id}`} className="dw-howto__toc-section dw-text-caption" data-tone={section.id === activeId ? "primary" : "muted"} aria-current={section.id === activeId ? "location" : undefined} onClick={() => setActiveId(section.id)}>
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                ))}
            </nav>
        </aside>
    );
});
